/**
 * Phase 33 — Execution Journey panel.
 *
 * Shows the BUY entry's complete decision + chase trail for a single trade:
 *   • Momentum tier (strong / normal / pullback) + buffer % used
 *   • Bid-ask spread at entry · ATM open interest at entry · lot size
 *   • Anchor LTP, initial limit price, current/last chase price
 *   • Chase status (ACTIVE / FILLED / TIMEOUT_CANCELLED / ABORTED_*)
 *   • Number of chase nudges applied (out of the 6-cycle budget)
 *
 * Used as an expandable second `<tr>` below the trade row in both
 * CandleBreakout (Section 5) and OIAutopilot (Section 6) tables.
 */
import React from "react";

const fmt = (v, d = 2) => (v == null ? "—" : Number(v).toFixed(d));
const formatIntK = (n) => {
  if (n == null) return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  if (x >= 1_00_000) return `${(x / 1_00_000).toFixed(1)}L`;
  if (x >= 1_000) return `${(x / 1_000).toFixed(1)}K`;
  return String(Math.round(x));
};

const TIER_COLORS = {
  strong:   { bg: "rgba(0, 200, 120, 0.12)", fg: "var(--bull)" },
  normal:   { bg: "rgba(180, 180, 180, 0.10)", fg: "var(--text-primary)" },
  pullback: { bg: "rgba(255, 182, 110, 0.12)", fg: "#FFB66E" },
};

const CHASE_STATUS_COLORS = {
  ACTIVE:                { fg: "var(--bull)",        label: "CHASING" },
  FILLED:                { fg: "var(--bull)",        label: "FILLED" },
  TIMEOUT_CANCELLED:     { fg: "var(--bear)",        label: "TIMEOUT · CANCELLED" },
  TIMEOUT_CANCEL_FAILED: { fg: "var(--bear)",        label: "TIMEOUT · CANCEL FAILED" },
  ABORTED_REJECTED:      { fg: "var(--bear)",        label: "REJECTED" },
  ABORTED_CANCELLED:     { fg: "var(--text-muted)",  label: "ABORTED" },
};

const Chip = ({ label, value, color, title }) => (
  <span
    className="inline-flex items-center gap-1 text-[10px] font-mono-data px-1.5 py-0.5 border"
    style={{ color: color || "var(--text-primary)", borderColor: "var(--border-color)" }}
    title={title}
  >
    <span className="text-muted-3 uppercase tracking-wider">{label}</span>
    <strong>{value}</strong>
  </span>
);

export const ExecutionJourney = ({ trade, colSpan = 9 }) => {
  if (!trade) return null;
  // 2026-08-24 — surfaces WHY a live-enabled trade stayed paper, or why a
  // live BUY/SELL/cancel failed. The backend persists these fields
  // (live_skip_reason: R:R-floor/red-candle-filter downgrade to paper,
  // decided BEFORE ever trying Upstox; live_error/live_error_message: a
  // live BUY that WAS attempted and got rejected/not-accepted by Upstox —
  // as of this fix, candle_breakout.py and oi_autopilot.py both now always
  // fall back to a paper trade carrying this detail instead of discarding
  // the attempt entirely (vwap_bounce_autopilot.py already did); live_sell_
  // error / buy_cancel_error: post-entry order failures) but nothing in the
  // UI ever rendered them — a trade with Live toggled ON that quietly
  // stayed paper looked identical to one that simply had no live attempt
  // at all. Chat: "Enabled Live for few hours. But Live order wasn't
  // placed. No error displayed in frontend."
  const liveIssue = trade.live_skip_reason
    || (trade.live_error ? [trade.live_error, trade.live_error_message].filter(Boolean).join(" — ") : null)
    || trade.live_sell_error || trade.buy_cancel_error;
  const liveIssueLabel = trade.live_skip_reason
    ? "STAYED PAPER"
    : trade.live_error
    ? "LIVE BUY REJECTED"
    : trade.live_sell_error
    ? "LIVE SELL ISSUE"
    : "CANCEL ISSUE";
  const tier = trade.momentum_tier;
  const tierStyle = TIER_COLORS[tier] || TIER_COLORS.normal;
  const buffer = trade.buy_buffer_pct_actual ?? trade.buy_buffer_pct;
  const spread = trade.spread_pct_at_entry;
  const oi = trade.atm_oi_at_entry;
  const lotSize = trade.live_qty;
  const anchor = trade.buy_anchor_ltp ?? trade.current_market_price;
  const initialBuyPrice = trade.live_buy_price ?? trade.limit_order_price;
  const lastChasePrice = trade.chase_last_price;
  const chaseSteps = trade.chase_steps;
  const chaseStatus = trade.chase_status;
  const avgFill = trade.live_avg_fill_price;
  const orderId = trade.live_buy_order_id;
  const chaseStatusStyle = CHASE_STATUS_COLORS[chaseStatus] || null;

  // Build chase progression: anchor LTP arrow initial buy arrow (each modify)
  const progression = [];
  if (anchor) progression.push({ label: "LTP", value: `₹${fmt(anchor)}` });
  if (initialBuyPrice && initialBuyPrice !== anchor) {
    progression.push({ label: "BUY", value: `₹${fmt(initialBuyPrice)}` });
  }
  if (lastChasePrice && lastChasePrice !== initialBuyPrice) {
    progression.push({ label: `STEP ${chaseSteps || ""}`, value: `₹${fmt(lastChasePrice)}` });
  }
  if (avgFill && Number(avgFill) > 0) {
    progression.push({ label: "FILL", value: `₹${fmt(avgFill)}`, fill: true });
  }

  // Nothing meaningful to show? Render a compact "paper trade" note — unless
  // there's a live issue to report, which is meaningful even with nothing else.
  if (!tier && !buffer && progression.length === 0 && !chaseStatus && !liveIssue) {
    return (
      <tr data-testid={`execution-journey-empty-${trade.id || trade._id || trade.symbol}`}>
        <td colSpan={colSpan} className="px-4 py-2 text-[10px] text-muted-3 font-mono-data" style={{ background: "rgba(255,255,255,0.015)" }}>
          No execution metadata captured for this trade (pre-Phase 33 entry or paper-only without dynamic buffer).
        </td>
      </tr>
    );
  }

  return (
    <tr
      data-testid={`execution-journey-${trade.id || trade._id || trade.symbol}`}
      style={{ background: "rgba(255,255,255,0.015)" }}
    >
      <td colSpan={colSpan} className="px-4 py-2">
        {liveIssue && (
          <div
            className="flex items-center gap-2 text-[10px] font-mono-data px-2 py-1 mb-1.5 border"
            style={{ color: "var(--bear)", borderColor: "var(--bear)", background: "rgba(255, 90, 90, 0.08)" }}
            data-testid={`exec-journey-live-issue-${trade.id || trade._id || trade.symbol}`}
          >
            <span className="uppercase tracking-wider font-semibold">{liveIssueLabel}</span>
            <span>{liveIssue}</span>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          {/* Tier badge */}
          {tier && (
            <span
              className="inline-flex items-center gap-1.5 text-[10px] font-mono-data px-2 py-0.5 border uppercase tracking-wider"
              style={{ color: tierStyle.fg, borderColor: tierStyle.fg, background: tierStyle.bg }}
              data-testid={`exec-journey-tier-${tier}`}
              title={`Momentum classification at entry`}
            >
              {tier}
            </span>
          )}
          {/* Buffer % */}
          {buffer != null && (
            <Chip label="BUFFER" value={`-${fmt(buffer, 2)}%`} title="Limit BUY discount applied below LTP" />
          )}
          {/* Spread */}
          {spread != null && (
            <Chip
              label="SPREAD"
              value={`${fmt(spread, 2)}%`}
              color={spread <= 2 ? "var(--bull)" : spread <= 4 ? "var(--text-primary)" : "var(--bear)"}
              title="Bid-ask spread at entry (relative to mid)"
            />
          )}
          {/* ATM OI */}
          {oi != null && <Chip label="OI" value={formatIntK(oi)} title="Open interest on the ATM strike" />}
          {/* Lot size */}
          {lotSize != null && <Chip label="QTY" value={lotSize} title="Total quantity (lot size × lots)" />}
          {/* 2026-08-26 — the actual number of lots placed (distinct from
              QTY above, which is total quantity) wasn't shown anywhere
              (chat: "Number of lots is not shown in Live Orders or
              anywhere, we need that to be displayed"). Resolved once at
              entry by trade_economics.resolve_stock_lot_count() and
              persisted as lots_placed (Candle Breakout/OI Autopilot/
              Sniper) or lots_per_trade (VWAP Bounce — same value, just a
              differently-named field on that setup's trade doc). */}
          {(trade.lots_placed ?? trade.lots_per_trade) != null && (
            <Chip
              label="LOTS"
              value={trade.lots_placed ?? trade.lots_per_trade}
              title="Number of lots placed at entry (monthly-cycle sizing)"
            />
          )}
          {/* Phase 36: Risk:Reward ratio */}
          {trade.risk_reward_ratio != null && (
            <Chip
              label="R:R"
              value={`${fmt(trade.risk_reward_ratio)}:1`}
              color={
                trade.risk_reward_ratio >= 1.0
                  ? "var(--bull)"
                  : trade.risk_reward_ratio >= 0.8
                  ? "var(--text-primary)"
                  : "var(--bear)"
              }
              title="target_pct / |stop_loss_pct| — Phase 36 floor is 0.8"
            />
          )}
          {/* Phase 34: price move since first-seen */}
          {trade.price_move_pct != null && (
            <Chip
              label="MOVE"
              value={`${trade.price_move_pct >= 0 ? "+" : ""}${fmt(trade.price_move_pct)}%`}
              color={
                trade.price_move_pct >= 40
                  ? "var(--bear)"
                  : trade.price_move_pct >= 20
                  ? "#FFB66E"
                  : "var(--text-primary)"
              }
              title={`Option premium move since first signal today (ref ₹${fmt(trade.reference_price)}). Phase 34 rejects at >= 50%`}
            />
          )}
          {/* Chase progression */}
          {progression.length > 0 && (
            <span className="inline-flex items-center gap-1 px-1">
              {progression.map((p, idx) => (
                <React.Fragment key={idx}>
                  {idx > 0 && <span className="text-muted-3">→</span>}
                  <span
                    className="text-[10px] font-mono-data"
                    style={{ color: p.fill ? "var(--bull)" : "var(--text-primary)" }}
                  >
                    <span className="text-muted-3 mr-0.5">{p.label}</span>
                    <strong>{p.value}</strong>
                  </span>
                </React.Fragment>
              ))}
            </span>
          )}
          {/* Chase status */}
          {chaseStatusStyle && (
            <span
              className="inline-flex items-center text-[10px] font-mono-data px-1.5 py-0.5 border uppercase tracking-wider"
              style={{ color: chaseStatusStyle.fg, borderColor: chaseStatusStyle.fg }}
              data-testid={`exec-journey-chase-status-${chaseStatus}`}
              title="Final state of the buy-price chase"
            >
              {chaseStatusStyle.label}
              {chaseSteps ? <span className="ml-1 text-muted-3">· {chaseSteps}/6</span> : null}
            </span>
          )}
          {orderId && (
            <span
              className="text-[9px] text-muted-3 font-mono-data ml-auto"
              title="Upstox broker order id"
            >
              ord · {orderId}
            </span>
          )}
        </div>
      </td>
    </tr>
  );
};

export default ExecutionJourney;
